import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import localforage from 'localforage';
import moment from 'moment';
import { AppDispatch, RootState, store } from '..';
import { Cliente } from '../../interface/Cliente';
import { Optometrica_table_interface } from '@bludata/common-interfaces';
import { Lenti1_table_interface } from '@bludata/common-interfaces';
import { Lenti3_table_interface } from '@bludata/common-interfaces';
import { Lenti2_table_interface } from '@bludata/common-interfaces';
import { Lentibusta_table_interface } from '@bludata/common-interfaces';
import { Busta_table_interface } from '@bludata/common-interfaces';
import { Lacbusta_table_interface } from '@bludata/common-interfaces';
import { ClientiInfo } from '../../interface/ClientiInfo';
import { ExtendedClientiTagTableInterface } from '../../clienti/menu/tag/ClientiMenuTag';
import { ClientiPopUpPrivacy_Interface } from '../../interface/ClientiPopUpPrivacy_Interface';
import ReactQueryInstance from '../../utility_objects/ReactQueryInstance';
import dayjs from 'dayjs';
import { setImportoTotale, setTotaleCarrello } from './CarrelloSlice';
import { NavigateFunction, useLocation } from 'react-router';
import RoutingTree from '@focus.cloud/src/RoutingTree';
import navigateToCliente from '@focus.cloud/src/utility_objects/navigateToCliente';
import { Appointment_interface } from '@focus.cloud/src/calendar/interfaces/Appointment';

export interface UnreadMessages {
  [contactRef: string]: {
    contactRef: string | undefined;
    phoneNumber: string;
    displayName: string | undefined;
    count: number;
  };
}

export interface Conversation {
  id: string;
  name: string;
  surname: string;
  phone: string;
  time: Date;
  unread: number;
  displayName: string;
  lastMessageText?: string;
  contentType?: string;
  schema?: 'whatsapp' | 'sms' | 'imo';
  messageType?: number; // 0 = received, 1 = sent
  clients: {
    personalCode: string;
    displayName: string;
    id: number;
  }[];
}

export interface ClientiSliceState {
  cliente: Cliente | null;
  clienteInfo: ClientiInfo | null;
  clienteListaTag: ExtendedClientiTagTableInterface[];
  clienteListaFamiliari: Cliente[];
  clienteListaInviatoDa: Cliente[];
  clientePopUpPrivacy: ClientiPopUpPrivacy_Interface;
  clientiLoadingError: boolean;
  clientiLoading: boolean;
  clienteEdit: Cliente;
  clienteNewInsert: boolean; //Serve per visualizzare eventuali dialog o messaggi post inserimento nuovo cliente, come la stampa della privacy (DialogWarningGdpr)
  hasPrivacy: boolean;
  totalRows: number;
  listinoCliente: string;
  filtroCognome: string;
  disattivaPriorCliente: boolean; //Disattiva pulsante precedente in ClientiRicerca se il prior torna -1, resettato ad ogni fetch cliente
  disattivaNextCliente: boolean; //Disattiva pulsante successivo in ClientiRicerca se il next torna -1, resettato ad ogni fetch cliente
  venditaDiretta: boolean;
  openPrivacy: boolean;
  openWarningGdpr: boolean;
  optometria: {
    optometriaSelected: number;
    optometriaEdit: Optometrica_table_interface | null;
  };
  busta: {
    bustaSelected: number;
    bustaEdit: Busta_table_interface | null;
    ordinaMontatura: boolean;
    ordinaLenteDx: boolean;
    ordinaLenteSx: boolean;
    stampaBusta: boolean;
    occhialeSelezionato: number;
    occhialeNonInLista: boolean;
    scontoPercentuale: number;
    montaturaValidata: boolean;
  };
  selectedCardLac: number;
  anamnesiLac: {
    anamnesiLacSelected: number;
    anamnesiLacEdit: Lenti1_table_interface | null;
  };
  provaLentiLac: {
    provaLentiLacSelected: number;
    provaLentiLacEdit: Lenti3_table_interface | null;
  };
  lacDefinitive: {
    lacDefinitiveSelected: number;
    lacDefinitiveEdit: Lenti2_table_interface | null;
    firstValidateCodiceLacDefDx: boolean;
    firstValidateCodiceLacDefSx: boolean;
  };
  fornituraLac: {
    fornituraLacSelected: number;
    fornituraLacEdit: Lentibusta_table_interface | null;
    fornituraLacEditListaLiquidi: Lacbusta_table_interface[] | [];
    ordinaLenteDx: boolean;
    ordinaLenteSx: boolean;
    firstValidateCodiceLacDx: boolean;
    firstValidateCodiceLacSx: boolean;
    utilizzaPrezziAggiornati: boolean;
    stampaFornituraLac: boolean;
    fornituraDuplicata?: boolean; // Indica se la fornitura è una duplicata
  };
  immagini: {
    immaginiSelected: number;
  };
  anagrafica: {
    comunicazioniPopUp: boolean;
    comunicazioniPopUpNote?: {
      dialogTitle: string;
      title: string;
      description: string;
      valutazione: string;
      showValutazione: boolean;
    };
    comunicazioneId?: number;
  };
  appuntamenti: {
    agendaDate: Date;
    hasStudio: boolean;
    searchDisponibility: boolean;
    openForm?: any;
    emailPopupForm: string;
    emailNotSetForm: string;
    searchPopupForm: boolean;
    formLoader: boolean;
    errorPopup: boolean;
    shopSignIsRecallOn: boolean;
    lockPointOfSale: boolean;
    lockService: boolean;
    lockUser: boolean;
    lockStudio: boolean;
    openLoader: boolean;
    rightClick?: any;
    emailPopup?: {
      id: string;
      emailSet: boolean;
      type: string;
      appointment: Appointment_interface;
      prevData?: Appointment_interface;
    };
    pointOfSales: {
      [x: string]: any;
    };
    services: {
      [x: string]: any;
    };
    studi: {
      [x: string]: any;
    };
    users: {
      [x: string]: any;
    };
  };
  notificheCloud: {
    [contactRef: string]: Conversation;
  };
  bluBookingAppointmentRef?: string;
}

const initialState: ClientiSliceState = {
  cliente: null,
  clienteInfo: null,
  clienteListaTag: [],
  clienteListaFamiliari: [],
  clienteListaInviatoDa: [],
  clientePopUpPrivacy: null,
  clientiLoading: false,
  clientiLoadingError: false,
  clienteEdit: null,
  clienteNewInsert: false,
  hasPrivacy: false,
  totalRows: 0,
  listinoCliente: '',
  filtroCognome: '',
  disattivaPriorCliente: false,
  disattivaNextCliente: false,
  venditaDiretta: false,
  openPrivacy: false,
  openWarningGdpr: false,
  optometria: {
    optometriaSelected: null,
    optometriaEdit: null,
  },
  busta: {
    bustaSelected: null,
    bustaEdit: null,
    ordinaMontatura: false,
    ordinaLenteDx: false,
    ordinaLenteSx: false,
    stampaBusta: false,
    occhialeSelezionato: null,
    occhialeNonInLista: false,
    scontoPercentuale: null,
    montaturaValidata: false,
  },
  selectedCardLac: 3,
  anamnesiLac: {
    anamnesiLacSelected: null,
    anamnesiLacEdit: null,
  },
  provaLentiLac: {
    provaLentiLacSelected: null,
    provaLentiLacEdit: null,
  },
  lacDefinitive: {
    lacDefinitiveSelected: null,
    lacDefinitiveEdit: null,
    firstValidateCodiceLacDefDx: false,
    firstValidateCodiceLacDefSx: false,
  },
  fornituraLac: {
    fornituraLacSelected: null,
    fornituraLacEdit: null,
    fornituraLacEditListaLiquidi: [],
    ordinaLenteDx: false,
    ordinaLenteSx: false,
    firstValidateCodiceLacDx: false,
    firstValidateCodiceLacSx: false,
    utilizzaPrezziAggiornati: true,
    stampaFornituraLac: false,
  },
  anagrafica: {
    comunicazioniPopUp: false,
  },
  immagini: {
    immaginiSelected: null,
  },
  appuntamenti: {
    lockPointOfSale: true,
    lockService: true,
    lockUser: true,
    lockStudio: true,
    agendaDate: dayjs().startOf('month').toDate(),
    hasStudio: true,
    searchDisponibility: false,
    emailPopupForm: '',
    emailNotSetForm: '',
    searchPopupForm: false,
    formLoader: false,
    errorPopup: false,
    shopSignIsRecallOn: false,
    openLoader: false,

    pointOfSales: {},
    services: {},
    studi: {},
    users: {},
  },
  notificheCloud: {},
};

const ClientiSlice = createSlice({
  name: 'clienti',
  initialState,
  reducers: {
    setClienti(state: ClientiSliceState, action: PayloadAction<Cliente>) {
      state.cliente = action.payload;
    },
    setClientiInfo(
      state: ClientiSliceState,
      action: PayloadAction<ClientiInfo>,
    ) {
      state.clienteInfo = action.payload;
    },
    setClienteListaTag(
      state: ClientiSliceState,
      action: PayloadAction<ExtendedClientiTagTableInterface[]>,
    ) {
      state.clienteListaTag = action.payload;
    },
    setClienteListaFamiliari(
      state: ClientiSliceState,
      action: PayloadAction<Cliente[]>,
    ) {
      state.clienteListaFamiliari = action.payload;
    },
    setClienteListaInviatoDa(
      state: ClientiSliceState,
      action: PayloadAction<Cliente[]>,
    ) {
      state.clienteListaInviatoDa = action.payload;
    },
    setClientePopUpPrivacy(
      state: ClientiSliceState,
      action: PayloadAction<ClientiPopUpPrivacy_Interface>,
    ) {
      state.clientePopUpPrivacy = action.payload;
    },
    setClientiEdit(state: ClientiSliceState, action: PayloadAction<Cliente>) {
      state.clienteEdit = action.payload;
    },
    setClienteNewInsert(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.clienteNewInsert = action.payload;
    },
    setPrivacy(state: ClientiSliceState, action: PayloadAction<boolean>) {
      state.hasPrivacy = action.payload;
    },
    setVenditaDiretta(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.venditaDiretta = action.payload;
    },
    setOpenPrivacy(state: ClientiSliceState, action: PayloadAction<boolean>) {
      state.openPrivacy = action.payload;
    },
    setOpenWarningGdpr(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.openWarningGdpr = action.payload;
    },
    setTotalRows(state: ClientiSliceState, action: PayloadAction<number>) {
      state.totalRows = action.payload;
    },
    setLoadingClienti(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.clientiLoading = action.payload;
    },
    setLoadingClientiError(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.clientiLoadingError = action.payload;
    },
    setOptometriaSelected(
      state: ClientiSliceState,
      action: PayloadAction<number | null>,
    ) {
      state.optometria.optometriaSelected = action.payload;
    },
    setOptometriaEdit(
      state: ClientiSliceState,
      action: PayloadAction<Optometrica_table_interface | null>,
    ) {
      state.optometria.optometriaEdit = action.payload;
    },
    setListinoCliente(state: ClientiSliceState, action: PayloadAction<string>) {
      state.listinoCliente = action.payload;
    },
    setFiltroCognome(state: ClientiSliceState, action: PayloadAction<string>) {
      state.filtroCognome = action.payload;
    },
    setDisattivaPriorCliente(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.disattivaPriorCliente = action.payload;
    },
    setDisattivaNextCliente(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.disattivaNextCliente = action.payload;
    },
    setBustaSelected(
      state: ClientiSliceState,
      action: PayloadAction<number | null>,
    ) {
      state.busta.bustaSelected = action.payload;
    },
    setSelectedCardLac(
      state: ClientiSliceState,
      action: PayloadAction<number>,
    ) {
      state.selectedCardLac = action.payload;
    },
    setAnamnesiLacSelected(
      state: ClientiSliceState,
      action: PayloadAction<number | null>,
    ) {
      state.anamnesiLac.anamnesiLacSelected = action.payload;
    },
    setAnamnesiLacEdit(
      state: ClientiSliceState,
      action: PayloadAction<Lenti1_table_interface | null>,
    ) {
      state.anamnesiLac.anamnesiLacEdit = action.payload;
    },
    setProvaLentiLacSelected(
      state: ClientiSliceState,
      action: PayloadAction<number | null>,
    ) {
      state.provaLentiLac.provaLentiLacSelected = action.payload;
    },
    setProvaLentiLacEdit(
      state: ClientiSliceState,
      action: PayloadAction<Lenti3_table_interface | null>,
    ) {
      state.provaLentiLac.provaLentiLacEdit = action.payload;
    },
    setLacDefinitiveSelected(
      state: ClientiSliceState,
      action: PayloadAction<number | null>,
    ) {
      state.lacDefinitive.lacDefinitiveSelected = action.payload;
    },
    setLacDefinitiveEdit(
      state: ClientiSliceState,
      action: PayloadAction<Lenti2_table_interface | null>,
    ) {
      state.lacDefinitive.lacDefinitiveEdit = action.payload;
    },
    setFornituraLacSelected(
      state: ClientiSliceState,
      action: PayloadAction<number | null>,
    ) {
      state.fornituraLac.fornituraLacSelected = action.payload;
    },
    setFornituraLacEdit(
      state: ClientiSliceState,
      action: PayloadAction<Lentibusta_table_interface | null>,
    ) {
      state.fornituraLac.fornituraLacEdit = action.payload;
    },
    setFornituraLacEditListaLiquidi(
      state: ClientiSliceState,
      action: PayloadAction<Lacbusta_table_interface[] | []>,
    ) {
      state.fornituraLac.fornituraLacEditListaLiquidi = action.payload;
    },

    setBustaEdit(
      state: ClientiSliceState,
      action: PayloadAction<Busta_table_interface | null>,
    ) {
      state.busta.bustaEdit = action.payload;
    },
    setBustaOrdinaMontatura(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.busta.ordinaMontatura = action.payload;
    },
    setBustaMontaturaValidata(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.busta.montaturaValidata = action.payload;
    },
    setBustaOrdinaLenteDx(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.busta.ordinaLenteDx = action.payload;
    },
    setBustaOrdinaLenteSx(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.busta.ordinaLenteSx = action.payload;
    },
    setLacOrdinaLenteDx(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.fornituraLac.ordinaLenteDx = action.payload;
    },
    setLacOrdinaLenteSx(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.fornituraLac.ordinaLenteSx = action.payload;
    },
    setUtilizzaPrezziAggiornati(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.fornituraLac.utilizzaPrezziAggiornati = action.payload;
    },
    setFirstValidateCodiceLacDx(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.fornituraLac.firstValidateCodiceLacDx = action.payload;
    },
    setFirstValidateCodiceLacSx(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.fornituraLac.firstValidateCodiceLacSx = action.payload;
    },
    setFirstValidateCodiceLacDefDx(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.lacDefinitive.firstValidateCodiceLacDefDx = action.payload;
    },
    setFirstValidateCodiceLacDefSx(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.lacDefinitive.firstValidateCodiceLacDefSx = action.payload;
    },
    setStampaBusta(state: ClientiSliceState, action: PayloadAction<boolean>) {
      state.busta.stampaBusta = action.payload;
    },
    setStampaFornituraLac(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.fornituraLac.stampaFornituraLac = action.payload;
    },
    setBustaOcchialeSelezionato(
      state: ClientiSliceState,
      action: PayloadAction<number>,
    ) {
      state.busta.occhialeSelezionato = action.payload;
    },
    setBustaOcchialeNonInLista(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.busta.occhialeNonInLista = action.payload;
    },
    setBustaScontoPercentuale(
      state: ClientiSliceState,
      action: PayloadAction<number>,
    ) {
      state.busta.scontoPercentuale = action.payload;
    },
    resetBusta(state: ClientiSliceState) {
      state.busta = initialState.busta;
    },
    resetFornituraLac(state: ClientiSliceState) {
      //c'è il codice per la gestione dell'ordine di imo, da resettare se si annulla l'ordine
      //@ts-expect-error global variable
      delete window.creazioneFornituraImo;
      //@ts-expect-error global variable
      delete window.creazioneFornituraImoCodiceFiliale;
      //Escluso fornituraLacSelected per mantenere il tab selezionato
      state.fornituraLac.fornituraLacEdit =
        initialState.fornituraLac.fornituraLacEdit;
      state.fornituraLac.fornituraLacEditListaLiquidi =
        initialState.fornituraLac.fornituraLacEditListaLiquidi;
      state.fornituraLac.ordinaLenteDx =
        initialState.fornituraLac.ordinaLenteDx;
      state.fornituraLac.ordinaLenteSx =
        initialState.fornituraLac.ordinaLenteSx;
      state.fornituraLac.firstValidateCodiceLacDx =
        initialState.fornituraLac.firstValidateCodiceLacDx;
      state.fornituraLac.firstValidateCodiceLacSx =
        initialState.fornituraLac.firstValidateCodiceLacSx;
      state.fornituraLac.utilizzaPrezziAggiornati =
        initialState.fornituraLac.utilizzaPrezziAggiornati;
      state.fornituraLac.stampaFornituraLac =
        initialState.fornituraLac.stampaFornituraLac;
    },
    resetLentiDef(state: ClientiSliceState) {
      state.lacDefinitive = initialState.lacDefinitive;
    },
    setComunicazioniPopUp(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.anagrafica.comunicazioniPopUp = action.payload;
    },
    setComunicazioniPopUpNote(
      state: ClientiSliceState,
      action: PayloadAction<
        | {
            dialogTitle: string;
            title: string;
            description: string;
            valutazione: string;
            showValutazione: boolean;
          }
        | undefined
      >,
    ) {
      state.anagrafica.comunicazioniPopUpNote = action.payload;
    },
    setComunicazioneId(
      state: ClientiSliceState,
      action: PayloadAction<number | undefined>,
    ) {
      state.anagrafica.comunicazioneId = action.payload;
    },
    setAgendaDate(state: ClientiSliceState, action: PayloadAction<Date>) {
      state.appuntamenti.agendaDate = action.payload;
    },

    setAgendaHasStudio(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.appuntamenti.hasStudio = action.payload;
    },
    setAgendaSearchDisponibility(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.appuntamenti.searchDisponibility = action.payload;
    },

    setAgendaOpenForm: (
      state: ClientiSliceState,
      action: PayloadAction<any | undefined>,
    ) => {
      state.appuntamenti.openForm = action.payload;
    },

    setAgendaEmailPopupForm(
      state: ClientiSliceState,
      action: PayloadAction<'' | 'create' | 'modify' | 'delete'>,
    ) {
      state.appuntamenti.emailPopupForm = action.payload;
    },
    setAgendaEmailNotSetForm(
      state: ClientiSliceState,
      action: PayloadAction<'' | 'create' | 'modify' | 'delete'>,
    ) {
      state.appuntamenti.emailNotSetForm = action.payload;
    },
    setAgendaSearchPopupForm(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.appuntamenti.searchPopupForm = action.payload;
    },
    setAgendaFormLoader(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.appuntamenti.formLoader = action.payload;
    },
    setAgendaErrorPopup(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.appuntamenti.errorPopup = action.payload;
    },
    setAgendaShopSignIsRecallOn(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.appuntamenti.shopSignIsRecallOn = action.payload;
    },
    setAgendaOpenLoader(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.appuntamenti.openLoader = action.payload;
    },
    setAgendaInitFilters(
      state: ClientiSliceState,
      action: PayloadAction<{
        services: any;
        pointOfSales: any;
        studi: any;
        users: any;
        // shopSigns: any;
      }>,
    ) {
      state.appuntamenti = {
        ...state.appuntamenti,
        ...action.payload,
      };
    },
    setLockPointOfSale(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      if (state.appuntamenti.lockPointOfSale !== action.payload)
        state.appuntamenti.lockPointOfSale = action.payload;
    },
    setLockService(state: ClientiSliceState, action: PayloadAction<boolean>) {
      if (state.appuntamenti.lockService !== action.payload)
        state.appuntamenti.lockService = action.payload;
    },
    setLockUser(state: ClientiSliceState, action: PayloadAction<boolean>) {
      if (state.appuntamenti.lockUser !== action.payload)
        state.appuntamenti.lockUser = action.payload;
    },

    setNotificheCloud(
      state: ClientiSliceState,
      action: PayloadAction<{
        [contactRef: string]: Conversation;
      }>,
    ) {
      state.notificheCloud = action.payload;
    },

    setLockStudio(state: ClientiSliceState, action: PayloadAction<boolean>) {
      if (state.appuntamenti.lockStudio !== action.payload)
        state.appuntamenti.lockStudio = action.payload;
    },
    setLocks(state: ClientiSliceState) {
      if (!state.appuntamenti.lockPointOfSale)
        state.appuntamenti.lockPointOfSale = true;
      if (!state.appuntamenti.lockService)
        state.appuntamenti.lockService = true;
      if (!state.appuntamenti.lockUser) state.appuntamenti.lockUser = true;
      if (!state.appuntamenti.lockStudio) state.appuntamenti.lockStudio = true;
    },
    setAgendaEmailPopup(
      state: ClientiSliceState,
      action: PayloadAction<
        | {
            id: string;
            emailSet: boolean;
            type: string;
            appointment: Appointment_interface;
            prevData?: Appointment_interface;
          }
        | undefined
      >,
    ) {
      state.appuntamenti.emailPopup = action.payload;
    },
    setAgendaRightClick(state: ClientiSliceState, action: PayloadAction<any>) {
      state.appuntamenti.rightClick = action.payload;
    },
    setImmaginiSelected(
      state: ClientiSliceState,
      action: PayloadAction<number | null>,
    ) {
      state.immagini.immaginiSelected = action.payload;
    },
    setBluBookingAppointmentRef(
      state: ClientiSliceState,
      action: PayloadAction<string | undefined>,
    ) {
      state.bluBookingAppointmentRef = action.payload;
    },
    setIsLacDuplicata(
      state: ClientiSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.fornituraLac.fornituraDuplicata = action.payload;
    },
    setEmptyClient(state: ClientiSliceState) {
      state.cliente = null;
      state.clienteInfo = null;
      state.clienteListaTag = [];
    },
  },
});

const hasPrivacy = (cliente: Cliente) => {
  if (!cliente) return false;

  if (cliente.hasOwnProperty('dataNascita') && cliente.dataNascita) {
    //per i minorenni non va mai salvata la privacy
    let now = new Date();
    if (moment(now).diff(moment(cliente.dataNascita), 'year') < 18) {
      return true;
    }
  }

  if (
    !cliente.hasOwnProperty('consensoInformato') ||
    !cliente.consensoInformato
  )
    return false;

  if (!cliente.hasOwnProperty('consensorev') || !cliente.consensorev) {
    return false;
  } else {
    let nomeFilePrivacy = cliente.consensorev.toLowerCase();

    if (
      nomeFilePrivacy.indexOf('gdpr') < 0 &&
      //controllo privacy coi task FOCUSWEB-1385
      nomeFilePrivacy.indexOf(' coi') < 0
    ) {
      return false;
    }
  }

  let dataPrivacy: any = cliente.dataPrivacy;

  if (!dataPrivacy) return false;
  let today = new Date();
  dataPrivacy = new Date(dataPrivacy);
  if (moment(today).diff(cliente.dataPrivacy, 'year') > 2) return false;

  return true;
};

export const toggleVenditaDiretta = (open: boolean) => {
  return (dispatch: AppDispatch) => {
    dispatch(setVenditaDiretta(open));
  };
};

let currentRequest = null;
let firstClientNotFound = false;

export const fetchClienti = (
  navigate: NavigateFunction,
  filters?: {
    occasionale?: boolean;
    id?: number;
  },
  noNavigate?: boolean,
) => {
  const codiceUtente = store.getState().root.loginData.CodiceUtente;
  const sessionLastCodCliStr = `lastCodCli_${codiceUtente}`;

  return async (dispatch: AppDispatch) => {
    let idParam: number;
    if (!filters) {
      //Se non è stato passato un id provo a caricare l'ultimo da localforage
      const savedId = await localforage.getItem<number>(sessionLastCodCliStr);
      idParam = savedId ?? 0;
    } else {
      //Se -1 vuol dire che non ho nessun cliente da caricare, non faccio nulla
      if (filters.id === -1) return;
      idParam = filters.id;
    }

    //Se ho un id da caricare
    if (idParam < 0) return;
    if (currentRequest) {
      currentRequest.cancel();
    }

    const CancelToken = axios.CancelToken;
    currentRequest = CancelToken.source();

    dispatch(setLoadingClienti(true));
    dispatch(setLoadingClientiError(false));
    dispatch(setDisattivaNextCliente(false));
    dispatch(setDisattivaPriorCliente(false));

    const errorHandler = () => {
      dispatch(setClientiInfo(null));
      dispatch(setLoadingClientiError(true));
      dispatch(setTotalRows(0));
      localforage.setItem(sessionLastCodCliStr, 0);
      navigate(RoutingTree.root.home.path);
    };

    axios
      .get(`/focusapi/cliente/${idParam}`, {
        cancelToken: currentRequest.token,
      })
      .then((response) => {
        if (response.data) {
          const {
            cliente,
            info,
            tag,
            familiari,
            clientiInviati,
            popUpPrivacy,
          } = response.data;
          const listino = info?.listino || '';
          const isClienteOccasionale =
            cliente?.codicePersonale.startsWith('000000');
          //Il server potrebbe essere più indietro del frontend se non si è aggiornato, per sicurezza verifico se ho i dati per le varie funzionalità
          //e in caso mando sulla console l'avviso di aggiornare il server.
          if (cliente) {
            ReactQueryInstance.removeQueries({
              queryKey: ['anagraficaClienti'],
            });
            dispatch(setClienti(cliente));
            // Non salvare l'ID del cliente occasionale in localforage per evitare che al prossimo avvio carichi l'occasionale
            if (!isClienteOccasionale) {
              localforage.setItem(sessionLastCodCliStr, cliente.id);
            }
            dispatch(setPrivacy(hasPrivacy(cliente)));
            dispatch(setListinoCliente(listino));
          }
          if (!isClienteOccasionale && info) {
            dispatch(setClientiInfo(info));
            dispatch(setTotaleCarrello(info.daPagare));
            dispatch(setImportoTotale(info.totaleCarrello));
          } else if (isClienteOccasionale) {
            // Reset badge e totali del cliente precedente
            dispatch(setClientiInfo(null));
            dispatch(setTotaleCarrello(0));
            dispatch(setImportoTotale(0));
          }
          if (tag) {
            dispatch(setClienteListaTag(tag));
          }
          if (familiari) {
            dispatch(setClienteListaFamiliari(familiari));
          }
          if (clientiInviati) {
            dispatch(setClienteListaInviatoDa(clientiInviati));
          }
          if (popUpPrivacy) {
            dispatch(setClientePopUpPrivacy(popUpPrivacy));
          }
          dispatch(setLoadingClienti(false));
          if (!noNavigate) {
            navigateToCliente(navigate, {
              idCliente: cliente.id,
            });
          }
        } else {
          errorHandler();
        }
      })
      .catch((e) => {
        if (axios.isCancel(e)) {
          console.error('Request canceled', e.message);
        } else {
          const idParamFirstClient = 0;
          if (e.response.data === 'Nessun cliente da caricare') {
            console.log('Nessun cliente trovato nel sistema.');
            ReactQueryInstance.removeQueries({
              queryKey: ['anagraficaClienti'],
            });
            localforage.setItem(sessionLastCodCliStr, idParamFirstClient);
            dispatch(setTotalRows(0));
            dispatch(setEmptyClient());
            navigateToCliente(navigate, {
              idCliente: idParamFirstClient,
            });
          } else if (e.response.data === 'Cliente non trovato') {
            dispatch(fetchClienti(navigate, { id: idParamFirstClient }));
          } else {
            errorHandler();
          }
        }
      })
      .finally(() => {
        dispatch(setLoadingClienti(false));
        currentRequest = null;
      });
  };
};

export const fetchClienteOccasionale = (
  filiale: string,
  spostaCarrello: boolean,
  navigate: NavigateFunction,
) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoadingClienti(true));
    dispatch(setLoadingClientiError(false));

    const errorHandler = () => {
      dispatch(setClientiInfo(null));
      dispatch(setLoadingClientiError(true));
      dispatch(setTotalRows(0));
    };
    axios
      .get(`/focusapi/tabella/clienti`, {
        params: {
          _dc: new Date().getTime(),
          page: 1,
          start: 0,
          limit: 1,
          sort: JSON.stringify([
            { property: 'cognome', direction: 'ASC' },
            { property: 'nome', direction: 'ASC' },
          ]),
          filter: JSON.stringify([
            {
              property: 'codicePersonale',
              operator: '=',
              value: `000000${filiale}`,
            },
          ]),
        },
      })
      .then((response) => {
        if (response.data) {
          const cliente = response.data.data[0];
          dispatch(fetchClienti(navigate, { id: cliente.id }, true));
          if (spostaCarrello) {
            // Guard difensivo: verifica profilo prima di navigare al carrello
            const profilo = getState().root.loginData.Profilo;
            if (
              !profilo?.clientiVisDatiSensibili ||
              !profilo?.clientiVisDatiPrezzi
            ) {
              navigate({
                pathname: RoutingTree.root.clienti.anagrafica.path,
                search: '?idCliente=' + cliente.id,
              });
            } else {
              navigate({
                pathname: RoutingTree.root.clienti.carrello.path,
                search: '?idCliente=' + cliente.id,
              });
            }
          } else {
            navigate({
              search: '?idCliente=' + cliente.id,
            });
          }
        } else {
          errorHandler();
        }
      })
      .catch(errorHandler)
      .finally(() => {
        dispatch(setLoadingClienti(false));
      });
  };
};

export const nextId = (
  id: number,
  filtroCognome: string,
  navigate: NavigateFunction,
) => {
  return (dispatch: AppDispatch) => {
    dispatch(setLoadingClienti(true));
    dispatch(setLoadingClientiError(false));

    const errorHandler = () => {
      dispatch(setLoadingClientiError(true));
      dispatch(setLoadingClienti(false));
      dispatch(setTotalRows(0));
    };

    let filter = [];
    filter.push({
      property: 'cognome',
      operator: '!=',
      value: '< CLIENTE OCCASIONALE >',
    });
    if (filtroCognome) {
      filter.push({
        property: 'cognome,nome,cognome2',
        operator: 'fulltext',
        value: filtroCognome,
      });
    }

    axios
      .get('/focusapi/utility/nextid', {
        params: {
          _dc: new Date().getTime(),
          sort: JSON.stringify([
            { property: 'cognome', direction: 'ASC' },
            { property: 'nome', direction: 'ASC' },
          ]),
          filter: JSON.stringify(filter),
          tablename: 'clienti',
          id: id,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          if (response.data === -1) {
            dispatch(setDisattivaNextCliente(true));
          } else {
            const nuoviParametri = new URLSearchParams();
            nuoviParametri.set('idCliente', response.data);
            navigate({
              search: nuoviParametri.toString(),
            });
          }
        } else {
          errorHandler();
        }
      })
      .catch(errorHandler)
      .finally(() => {
        dispatch(setLoadingClienti(false));
      });
  };
};

export const prevId = (
  id: number,
  filtroCognome: string,
  navigate: NavigateFunction,
) => {
  return (dispatch: AppDispatch) => {
    dispatch(setLoadingClienti(true));
    dispatch(setLoadingClientiError(false));

    const errorHandler = () => {
      dispatch(setLoadingClientiError(true));
      dispatch(setLoadingClienti(false));
      dispatch(setTotalRows(0));
    };

    let filter = [];
    filter.push({
      property: 'cognome',
      operator: '!=',
      value: '< CLIENTE OCCASIONALE >',
    });
    if (filtroCognome) {
      filter.push({
        property: 'cognome,nome,cognome2',
        operator: 'fulltext',
        value: filtroCognome,
      });
    }

    axios
      .get('/focusapi/utility/priorid', {
        params: {
          _dc: new Date().getTime(),
          sort: JSON.stringify([
            { property: 'cognome', direction: 'ASC' },
            { property: 'nome', direction: 'ASC' },
          ]),
          filter: JSON.stringify(filter),
          tablename: 'clienti',
          id: id,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          if (response.data === -1) {
            dispatch(setDisattivaPriorCliente(true));
          } else {
            const nuoviParametri = new URLSearchParams();
            nuoviParametri.set('idCliente', response.data);
            navigate({
              search: nuoviParametri.toString(),
            });
          }
        } else {
          errorHandler();
        }
      })
      .catch(errorHandler)
      .finally(() => {
        dispatch(setLoadingClienti(false));
      });
  };
};

export const updateCliente = (noTimeout?: boolean) => {
  if (noTimeout) {
    window.dispatchEvent(new Event('clientUpdate'));
    return;
  }
  setTimeout(() => {
    window.dispatchEvent(new Event('clientUpdate'));
  }, 400);
};

export const {
  setClienti,
  setClientiInfo,
  setClienteListaTag,
  setClienteListaFamiliari,
  setClienteListaInviatoDa,
  setClientePopUpPrivacy,
  setVenditaDiretta,
  setOpenPrivacy,
  setOpenWarningGdpr,
  setClientiEdit,
  setClienteNewInsert,
  setPrivacy,
  setTotalRows,
  setOptometriaSelected,
  setOptometriaEdit,
  setListinoCliente,
  setBustaSelected,
  setLoadingClientiError,
  setLoadingClienti,
  setFiltroCognome,
  setDisattivaPriorCliente,
  setDisattivaNextCliente,
  setEmptyClient,
  //LAC
  setSelectedCardLac,
  setAnamnesiLacSelected,
  setAnamnesiLacEdit,
  setProvaLentiLacSelected,
  setProvaLentiLacEdit,
  setLacDefinitiveSelected,
  setLacDefinitiveEdit,
  setFornituraLacSelected,
  setFornituraLacEdit,
  setIsLacDuplicata,
  setFornituraLacEditListaLiquidi,
  setLacOrdinaLenteDx,
  setLacOrdinaLenteSx,
  setFirstValidateCodiceLacDx,
  setFirstValidateCodiceLacSx,
  setUtilizzaPrezziAggiornati,
  setFirstValidateCodiceLacDefDx,
  setFirstValidateCodiceLacDefSx,
  resetFornituraLac,
  resetLentiDef,
  setStampaFornituraLac,
  //BUSTA
  setBustaEdit,
  setBustaOrdinaMontatura,
  setBustaMontaturaValidata,
  setBustaOrdinaLenteDx,
  setBustaOrdinaLenteSx,
  setStampaBusta,
  setBustaOcchialeSelezionato,
  setBustaScontoPercentuale,
  setBustaOcchialeNonInLista,
  resetBusta,
  setComunicazioniPopUp,
  setComunicazioniPopUpNote,
  setComunicazioneId,

  // APPUNTAMENTI
  setAgendaDate,
  setAgendaHasStudio,
  setAgendaSearchDisponibility,
  setAgendaOpenForm,
  setAgendaEmailPopupForm,
  setAgendaEmailNotSetForm,
  setAgendaSearchPopupForm,
  setAgendaFormLoader,
  setAgendaErrorPopup,
  setAgendaShopSignIsRecallOn,
  setAgendaOpenLoader,
  setAgendaInitFilters,
  setLockPointOfSale,
  setLockService,
  setLockUser,
  setLockStudio,
  setLocks,
  setAgendaEmailPopup,
  setAgendaRightClick,

  // IMMAGINI
  setImmaginiSelected,

  //NOTIFICHE CLOUD
  setNotificheCloud,

  setBluBookingAppointmentRef,
} = ClientiSlice.actions;

export default ClientiSlice.reducer;
